import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Heart, Users, Lightbulb, Rocket, Globe, CheckCircle, Database, Smartphone, Cloud } from 'lucide-react';

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };


  return (
    <div className="pt-24 pb-20 bg-gradient-to-b from-slate-50 to-white min-h-screen relative overflow-hidden">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle, #1060BE 2px, transparent 2px)`,
          backgroundSize: '50px 50px',
          backgroundPosition: '50px 50px'
        }} />
      </div>

      <div className="max-w-content mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Hero Section */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-20 text-center"
        >
          <motion.h1 
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
            }}
            className="text-4xl md:text-5xl lg:text-6xl font-semibold text-slate-900 mb-6 tracking-tight bg-clip-text"
          >
            About Kaeliv Technologies
          </motion.h1>
          <motion.p 
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
            }}
            className="text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto"
          >
            Engineering software systems with long-term thinking. Quality and adaptability over quick delivery.
          </motion.p>
        </motion.div>

        {/* Technologies & Focus Areas */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4 tracking-tight"
            >
              Technologies We Master
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg text-slate-600 max-w-2xl mx-auto"
            >
              Building modern solutions with cutting-edge technologies and best practices
            </motion.p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: <Globe className="text-primary" size={28} />, 
                title: "Web Development", 
                desc: "React, Next.js, Node.js, TypeScript, Tailwind CSS",
                gradient: "from-blue-50 to-cyan-50"
              },
              { 
                icon: <Smartphone className="text-primary" size={28} />, 
                title: "Mobile Apps", 
                desc: "React Native, Flutter, iOS & Android Development",
                gradient: "from-purple-50 to-pink-50"
              },
              { 
                icon: <Database className="text-primary" size={28} />, 
                title: "Backend & Database", 
                desc: "PostgreSQL, MongoDB, Redis, REST & GraphQL APIs",
                gradient: "from-green-50 to-emerald-50"
              },
              { 
                icon: <Cloud className="text-primary" size={28} />, 
                title: "Cloud & DevOps", 
                desc: "AWS, Docker, CI/CD, Kubernetes, Serverless",
                gradient: "from-orange-50 to-amber-50"
              }
            ].map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02, rotate: 0 }}
                className={`bg-gradient-to-br ${tech.gradient} p-6 rounded-2xl border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden`}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-2xl -mr-16 -mt-16 group-hover:bg-primary/10 transition-colors" />
                <div className="relative z-10">
                  <div className="mb-4 p-3 bg-white/60 rounded-xl w-fit group-hover:bg-white/80 transition-colors group-hover:scale-110 transform duration-300">
                    {tech.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 tracking-tight group-hover:text-primary transition-colors">
                    {tech.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {tech.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Company Name Meaning Section */}
        <motion.div 
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ y: -8, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
          className="mb-20 p-8 md:p-12 bg-gradient-to-br from-white to-slate-50 rounded-2xl border border-slate-200 shadow-lg group cursor-default relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -mr-32 -mt-32" />
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            initial={false}
          />
          <div className="mb-6 relative z-10 flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors">
              <Lightbulb className="text-primary" size={32} />
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-8 tracking-tight relative z-10 group-hover:text-primary transition-colors">
            What Does "Kaeliv" Mean?
          </h2>
          <div className="grid md:grid-cols-2 gap-6 relative z-10">
            {/* Kay - Kinetic */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="p-6 bg-gradient-to-br from-primary/5 to-primary/0 rounded-xl border border-primary/20"
            >
              <h3 className="text-2xl font-bold text-primary mb-4">
                <span className="text-slate-900">Kay</span> — Kinetic
              </h3>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-1" size={18} />
                  <span>Technology should be able to move.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-1" size={18} />
                  <span>Adapt as needs change.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-1" size={18} />
                  <span>Grow without breaking.</span>
                </li>
              </ul>
            </motion.div>

            {/* eliv - Elevate */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="p-6 bg-gradient-to-br from-primary/5 to-primary/0 rounded-xl border border-primary/20"
            >
              <h3 className="text-2xl font-bold text-primary mb-4">
                <span className="text-slate-900">eliv</span> — Elevate
              </h3>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-1" size={18} />
                  <span>Products shouldn't just work.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-1" size={18} />
                  <span>They should elevate the experience</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-1" size={18} />
                  <span>And the business behind them.</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </motion.div>

        {/* Our Story */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20 text-center"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px bg-gradient-to-r from-primary to-transparent flex-1" />
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">Our Story</h2>
            <div className="h-px bg-gradient-to-l from-primary to-transparent flex-1" />
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto"
          >
            Kaeliv Technologies was founded to bridge the gap between complex technology and user-friendly business solutions. 
            We're a team of passionate developers, designers, and strategists focused on quality—building software that empowers, not complicates.
          </motion.p>
        </motion.div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            whileHover={{ y: -8, scale: 1.02 }}
            className="p-8 bg-gradient-to-br from-white to-slate-50 rounded-2xl border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-2xl -mr-16 -mt-16 group-hover:bg-primary/10 transition-colors" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors">
                  <Target className="text-primary" size={28} />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 tracking-tight group-hover:text-primary transition-colors">Our Mission</h3>
              </div>
              <p className="text-slate-600 leading-relaxed text-lg">
                To empower businesses with software that is not only functional but reliable and maintainable. We create 
                solutions that solve real problems, enhance productivity, and drive measurable business outcomes over the long term.
              </p>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            whileHover={{ y: -8, scale: 1.02 }}
            className="p-8 bg-gradient-to-br from-white to-slate-50 rounded-2xl border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-2xl -mr-16 -mt-16 group-hover:bg-primary/10 transition-colors" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors">
                  <Eye className="text-primary" size={28} />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 tracking-tight group-hover:text-primary transition-colors">Our Vision</h3>
              </div>
              <p className="text-slate-600 leading-relaxed text-lg">
                To become a trusted partner for enterprise software development. We envision technology that seamlessly integrates 
                into business operations, making them more efficient, adaptable, and sustainable.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Values */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-12">
            <div className="h-px bg-gradient-to-r from-primary to-transparent flex-1" />
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">Our Core Values</h2>
            <div className="h-px bg-gradient-to-l from-primary to-transparent flex-1" />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <Heart className="text-primary" size={28} />, title: "Quality", desc: "Every line of code reflects our commitment to engineering excellence and maintainability." },
              { icon: <Rocket className="text-primary" size={28} />, title: "Adaptability", desc: "We build systems that evolve with your business needs, not against them." },
              { icon: <Users className="text-primary" size={28} />, title: "Partnership", desc: "Great products emerge from collaborative relationships built on trust and clear communication." }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.03 }}
                className="p-8 bg-white rounded-2xl border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full blur-2xl -mr-12 -mt-12 group-hover:bg-primary/10 transition-colors" />
                <div className="relative z-10 space-y-4">
                  <div className="p-4 bg-primary/10 rounded-xl w-fit group-hover:bg-primary/20 transition-colors group-hover:scale-110 transform duration-300">
                    {value.icon}
                  </div>
                  <h4 className="text-xl font-semibold text-slate-900 tracking-tight group-hover:text-primary transition-colors">{value.title}</h4>
                  <p className="text-slate-600 leading-relaxed">{value.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;